import type { ReportScope } from "@/types/reporting";

export const toErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

export const sortedIdKey = (ids: number[]): string =>
  [...ids].sort((first, second) => first - second).join(",");

// Serializing the filters as-is is stable because buildReportingScopeFilters
// sorts every id, so the same selection always yields the same filters. The
// field stays in the key: the same ids at different hierarchy levels are
// different scopes.
export const reportScopeKey = ({
  endDate,
  scopeFilters,
  startDate,
}: ReportScope): string =>
  `${JSON.stringify(scopeFilters)}:${startDate}:${endDate}`;

type GuardResult =
  | { outcome: "cached" }
  | { outcome: "in-flight" }
  | { outcome: "proceed"; requestId: number };

// Shared concurrency guard for all reporting stores.
//
// Encapsulates three invariants:
//   1. Only the most recent scope's result commits (latestRequestId token).
//   2. Same-scope re-calls while a fetch is in-flight skip without starting a
//      duplicate request and without bumping the token — preserving the
//      in-flight request's ability to commit its own result.
//   3. Switching back to a cached scope clears any stale pendingScopeKey so
//      the displaced scope doesn't become permanently unfetchable.
export const createRequestGuard = () => {
  let latestRequestId = 0;
  let pendingScopeKey: string | null = null;

  return {
    check: (scopeKey: string, loadedScopeKey: string | null): GuardResult => {
      if (loadedScopeKey === scopeKey) {
        latestRequestId += 1;
        pendingScopeKey = null;
        return { outcome: "cached" };
      }
      if (pendingScopeKey === scopeKey) {
        return { outcome: "in-flight" };
      }
      latestRequestId += 1;
      pendingScopeKey = scopeKey;
      return { outcome: "proceed", requestId: latestRequestId };
    },
    isStale: (requestId: number): boolean => requestId !== latestRequestId,
    clearPending: (scopeKey: string) => {
      if (pendingScopeKey === scopeKey) pendingScopeKey = null;
    },
    invalidate: () => {
      latestRequestId += 1;
      pendingScopeKey = null;
    },
  };
};
